/** PUT a file straight to S3 via a presigned URL, reporting percent progress.
 *  Shared by the Documents page and the holding-side "upload & attach" flow.
 *
 *  `options.timeoutMs` is opt-in: with none given the request has no timeout, exactly as
 *  before, so every existing caller behaves as it did (pinned in `upload.test.ts`). The
 *  intake form passes one, because a person on a bad connection is better told than left
 *  watching a bar that never moves.
 *
 *  A rejection carries `status` (the HTTP status, or 0 when the request never got an
 *  answer) so a caller can tell a refused PUT from a dropped connection. */
export interface UploadOptions { timeoutMs?: number }

export class UploadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
  }
}

export function uploadToS3(
  url: string, file: File, onProgress: (pct: number) => void, options?: UploadOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    // A presigned or CDN link is a capability URL. This flag keeps the browser's cookies off a
    // CROSS-ORIGIN request, which is what the documents CDN is. It does nothing for a same-origin
    // URL (the browser always sends those cookies), so it is not what keeps cookies from a link
    // on this site's own origin.
    xhr.withCredentials = false;
    if (options?.timeoutMs !== undefined) xhr.timeout = options.timeoutMs;
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload  = () => xhr.status < 400 ? resolve() : reject(new UploadError(`S3 upload failed: ${xhr.status}`, xhr.status));
    xhr.onerror = () => reject(new UploadError('Network error during upload', 0));
    xhr.ontimeout = () => reject(new UploadError('Upload timed out', 0));
    xhr.send(file);
  });
}
